import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LogoVertical from './LogoVertical';
import { 
  Home, 
  Package, 
  Users, 
  FileText, 
  Shield, 
  Eye,
  BarChart3,
  LogOut,
  Database
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  const menuItems = [
    {
      path: '/',
      icon: Home,
      label: 'Dashboard',
      description: 'Painel principal',
      allowedRoles: ['admin', 'farmaceutico', 'balconista']
    },
    {
      path: '/visitantes',
      icon: Eye,
      label: 'Área de Visitantes',
      description: 'Visualizar medicamentos',
      allowedRoles: ['visitante']
    },
    {
      path: '/medicamentos',
      icon: Package,
      label: 'Medicamentos',
      description: 'Gerenciar medicamentos',
      allowedRoles: ['admin', 'farmaceutico', 'balconista']
    },
    {
      path: '/medicamentos-registrados',
      icon: Database,
      label: 'Medicamentos Registrados',
      description: 'Gerenciar cadastro de medicamentos',
      allowedRoles: ['admin', 'farmaceutico']
    },
    {
      path: '/pacientes',
      icon: Users,
      label: 'Pacientes',
      description: 'Gerenciar pacientes',
      allowedRoles: ['admin', 'farmaceutico', 'balconista']
    },
    {
      path: '/liberacoes',
      icon: FileText,
      label: 'Liberações',
      description: 'Liberações de medicamentos',
      allowedRoles: ['admin', 'farmaceutico', 'balconista']
    },
    {
      path: '/relatorios',
      icon: BarChart3,
      label: 'Relatórios',
      description: 'Relatórios de consumo',
      allowedRoles: ['admin', 'farmaceutico']
    },
    {
      path: '/usuarios',
      icon: Shield,
      label: 'Gestão de Usuários',
      description: 'Gerenciar usuários do sistema',
      allowedRoles: ['admin']
    }
  ];

  const handleLogout = () => {
    logout();
  };

  const isActive = (path) => {
    if (path === '/medicamentos-registrados') {
      return location.pathname === path || location.pathname.startsWith('/medicamentos-registrados/');
    }
    return location.pathname === path;
  };

  return (
    <div 
      className="sidebar"
      style={{
        width: isExpanded ? '250px' : '60px',
        height: '100vh',
        backgroundColor: '#2c3e50',
        color: 'white',
        position: 'fixed',
        left: 0,
        top: 0,
        transition: 'width 0.3s ease',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
      }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Header */}
      <div style={{
        padding: isExpanded ? '20px 15px' : '15px',
        borderBottom: '1px solid #34495e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minHeight: isExpanded ? '140px' : '80px',
        transition: 'all 0.3s ease'
      }}>
        <LogoVertical 
          size={isExpanded ? "medium" : "small"} 
          color="white"
        />
      </div>

      {/* Menu Items */}
      <nav style={{ flex: 1, padding: '20px 0' }}>
        {menuItems
          .filter(item => item.allowedRoles.includes(user?.cargo))
          .map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 15px',
                color: 'white',
                textDecoration: 'none',
                backgroundColor: active ? '#3498db' : 'transparent',
                borderLeft: active ? '4px solid #2980b9' : '4px solid transparent',
                transition: 'all 0.2s ease',
                position: 'relative',
                gap: '15px'
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = '#34495e';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <Icon size={20} style={{ flexShrink: 0 }} />
              
              {isExpanded && (
                <div style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  opacity: isExpanded ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}>
                  <div style={{ fontWeight: '500' }}>{item.label}</div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#bdc3c7',
                    marginTop: '2px'
                  }}>
                    {item.description}
                  </div>
                </div>
              )}

              {/* Tooltip para modo colapsado */}
              {!isExpanded && (
                <div
                  style={{
                    position: 'absolute',
                    left: '65px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: '#2c3e50',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    boxShadow: '2px 2px 10px rgba(0,0,0,0.2)',
                    border: '1px solid #34495e',
                    opacity: 0,
                    visibility: 'hidden',
                    transition: 'all 0.2s ease',
                    zIndex: 1001,
                    pointerEvents: 'none'
                  }}
                  className="sidebar-tooltip"
                >
                  <div style={{ fontWeight: '500' }}>{item.label}</div>
                  <div style={{ color: '#bdc3c7', fontSize: '10px' }}>
                    {item.description}
                  </div>
                  
                  {/* Seta do tooltip */}
                  <div style={{
                    position: 'absolute',
                    left: '-5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderTop: '5px solid transparent',
                    borderBottom: '5px solid transparent',
                    borderRight: '5px solid #2c3e50'
                  }} />
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Info e Logout */}
      <div style={{
        borderTop: '1px solid #34495e',
        padding: '15px'
      }}>
        {/* User Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '15px',
          padding: '8px',
          backgroundColor: '#34495e',
          borderRadius: '6px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#3498db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            flexShrink: 0
          }}>
            {user?.nome?.charAt(0).toUpperCase()}
          </div>
          
          {isExpanded && (
            <div style={{ 
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}>
              <div style={{ fontWeight: '500' }}>{user?.nome}</div>
              <div style={{ color: '#bdc3c7', textTransform: 'capitalize' }}>
                {user?.cargo}
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            width: '100%',
            padding: '10px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#e74c3c',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            fontSize: '14px',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e74c3c';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#e74c3c';
          }}
        >
          <LogOut size={18} style={{ flexShrink: 0 }} />
          
          {isExpanded && (
            <span style={{ whiteSpace: 'nowrap' }}>Sair do Sistema</span>
          )}

          {/* Tooltip para logout no modo colapsado */}
          {!isExpanded && (
            <div
              style={{
                position: 'absolute',
                left: '65px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: '#e74c3c',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                boxShadow: '2px 2px 10px rgba(0,0,0,0.2)',
                opacity: 0,
                visibility: 'hidden',
                transition: 'all 0.2s ease',
                zIndex: 1001,
                pointerEvents: 'none'
              }}
              className="sidebar-tooltip"
            >
              Sair do Sistema
              
              {/* Seta do tooltip */}
              <div style={{
                position: 'absolute',
                left: '-5px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderRight: '5px solid #e74c3c'
              }} />
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;